import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useId, useMemo, useState } from "react";
import { toast } from "sonner";
import { useUserData } from "../../contexts/user-data-context";
import { useClientTranslations } from "../../lib/i18n/client";
import { ProfileModal } from "./ProfileModal";

interface ChangePasswordModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export function ChangePasswordModal({
	isOpen,
	onClose,
}: ChangePasswordModalProps) {
	const currentPasswordId = useId();
	const newPasswordId = useId();
	const confirmPasswordId = useId();

	const { userData } = useUserData();
	const { user } = useAuth0();
	const { t } = useClientTranslations("profile");
	const { t: tCommon } = useClientTranslations("common");

	const userAuth0Id =
		(userData && "auth0Id" in userData && typeof userData.auth0Id === "string"
			? userData.auth0Id
			: undefined) ||
		user?.sub ||
		"";
	const isGoogleAccount = useMemo(
		() =>
			typeof userAuth0Id === "string" &&
			userAuth0Id &&
			!userAuth0Id.startsWith("auth0|"),
		[userAuth0Id],
	);

	// Local answers state for the question component
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");

	const handleSave = useCallback(async () => {
		if (isGoogleAccount) return; // Save disabled

		// Basic validation
		if (!currentPassword || !newPassword || !confirmPassword) {
			toast.error(t("messages.fillAllPasswordFields"));
			return;
		}

		if (newPassword !== confirmPassword) {
			toast.error(t("messages.passwordsDoNotMatch"));
			return;
		}

		// Log the password change attempt (for now, just console.log as requested)
		console.log("Password change requested:", {
			currentPassword: "***",
			newPassword: "***",
			userAuth0Id,
		});

		toast.success(tCommon("toast.success.passwordChangeRequested"));
		onClose();

		// TODO: When backend endpoint is ready, uncomment this:
		/*
		try {
			setLoading(true);
			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/api/user/change-password`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						currentPassword,
						newPassword,
					}),
				},
			);
			const result = await response.json().catch(() => ({}));
			if (response.ok) {
				toast.success(t("messages.passwordChanged"));
				onClose();
			} else {
				throw new Error(result?.error || "Failed to change password");
			}
		} catch (error) {
			console.error("Error changing password:", error);
			toast.error(t("messages.failedToChangePassword"));
		} finally {
			setLoading(false);
		}
		*/
	}, [
		currentPassword,
		newPassword,
		confirmPassword,
		isGoogleAccount,
		t,
		onClose,
		userAuth0Id,
		tCommon,
	]);

	return (
		<ProfileModal
			isOpen={isOpen}
			onClose={onClose}
			onSave={handleSave}
			title={t("changePassword")}
			saveButtonText={t("common.save")}
			hideSaveButton={!!isGoogleAccount}
		>
			{isGoogleAccount ? (
				<div style={{ padding: "1rem", textAlign: "center" }}>
					<h3>{t("password.googleAccountDetected")}</h3>
					<p>{t("password.googleAccountMessage")}</p>
					<a
						href="https://myaccount.google.com/security"
						target="_blank"
						rel="noopener noreferrer"
						style={{
							display: "inline-block",
							marginTop: "1rem",
							padding: "0.5rem 1rem",
							backgroundColor: "#4285f4",
							color: "white",
							borderRadius: "0.25rem",
							textDecoration: "none",
						}}
					>
						{t("password.goToGoogleSettings")}
					</a>
				</div>
			) : (
				<div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
					<div>
						<label
							htmlFor={currentPasswordId}
							style={{ display: "block", marginBottom: "0.5rem" }}
						>
							{t("password.currentPassword")}
						</label>
						<input
							id={currentPasswordId}
							type="password"
							value={currentPassword}
							onChange={(e) => setCurrentPassword(e.target.value)}
							placeholder={t("password.enterCurrentPassword")}
							style={{
								width: "100%",
								padding: "0.5rem",
								borderRadius: "0.25rem",
								border: "1px solid #d1d5db",
							}}
						/>
					</div>
					<div>
						<label
							htmlFor={newPasswordId}
							style={{ display: "block", marginBottom: "0.5rem" }}
						>
							{t("password.newPassword")}
						</label>
						<input
							id={newPasswordId}
							type="password"
							value={newPassword}
							onChange={(e) => setNewPassword(e.target.value)}
							placeholder={t("password.enterNewPassword")}
							style={{
								width: "100%",
								padding: "0.5rem",
								borderRadius: "0.25rem",
								border: "1px solid #d1d5db",
							}}
						/>
					</div>
					<div>
						<label
							htmlFor={confirmPasswordId}
							style={{ display: "block", marginBottom: "0.5rem" }}
						>
							{t("password.confirmPassword")}
						</label>
						<input
							id={confirmPasswordId}
							type="password"
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
							placeholder={t("password.confirmNewPassword")}
							style={{
								width: "100%",
								padding: "0.5rem",
								borderRadius: "0.25rem",
								border: "1px solid #d1d5db",
							}}
						/>
					</div>
				</div>
			)}
		</ProfileModal>
	);
}
